// ============================================================================
// Send to… and Clear, wired once for both screens that list work — Home and
// My Work, phone and desktop (item #835). Each action lands the row in Done
// today with Undo; Undo puts it back on this person's list.
// ============================================================================
import { useCallback, useState } from 'react';
import { errorMessage } from '../../shared/errors';
import { useRoutablePeople } from '../routing/hooks/use-routing-data';
import { clearRow, sendTo, undoFor, undoRow } from './hand-off';
import type { WorkRow } from './home-work';
import type { DoneItem } from './done-today';
import { ClearSheet, SendToSheet } from './work-list';

export function useRowActions({
  eid,
  add,
  forget,
  reload,
  say,
}: {
  eid: string | null;
  add: (d: DoneItem) => void;
  forget: (key: string) => void;
  reload: () => void;
  say: (m: string) => void;
}) {
  const { people, isLoading: peopleLoading } = useRoutablePeople();
  const [sending, setSending] = useState<WorkRow | null>(null);
  const [clearing, setClearing] = useState<WorkRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setSending(null);
    setClearing(null);
    setError(null);
  };

  const confirmClear = async (why: string) => {
    if (!clearing) return;
    const row = clearing;
    setBusy(true);
    setError(null);
    try {
      await clearRow(row);
      add({
        key: row.key,
        label: `Cleared — ${row.name}${why.trim() ? ` (${why.trim()})` : ''}`,
        projectId: null,
        at: new Date().toISOString(),
        undo: undoFor(row),
      });
      close();
      reload();
      say('Cleared from your list.');
    } catch (e) {
      setError(errorMessage(e, 'That row could not be cleared.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmSend = async (to: { employee_id: string; name: string }, why: string) => {
    if (!sending) return;
    const row = sending;
    setBusy(true);
    setError(null);
    try {
      await sendTo(row, to.employee_id, why);
      add({
        key: row.key,
        label: `Sent to ${to.name} — ${row.name}${why.trim() ? ` (${why.trim()})` : ''}`,
        projectId: null,
        at: new Date().toISOString(),
        undo: undoFor(row),
      });
      close();
      reload();
      say(`Sent. It's off your list and on ${to.name.split(' ')[0]}'s.`);
    } catch (e) {
      setError(errorMessage(e, 'That row could not be sent.'));
    } finally {
      setBusy(false);
    }
  };

  /** Undo for a row that was cleared or sent on. Returns false if it is not one. */
  const undo = useCallback(
    async (d: DoneItem): Promise<boolean> => {
      if (!d.undo) return false;
      try {
        await undoRow(d.undo);
        forget(d.key);
        reload();
        say(d.label.startsWith('Sent to') ? 'Back on your list. The person you sent it to still has their copy.' : 'Back on your list.');
      } catch (e) {
        say(errorMessage(e, 'That row could not be put back.'));
      }
      return true;
    },
    [forget, reload, say],
  );

  const sheets = (
    <>
      {clearing && <ClearSheet row={clearing} busy={busy} error={error} onCancel={close} onConfirm={(w) => void confirmClear(w)} />}
      {sending && (
        <SendToSheet
          row={sending}
          myEid={eid}
          people={people}
          peopleLoading={peopleLoading}
          busy={busy}
          error={error}
          onCancel={close}
          onConfirm={(to, w) => void confirmSend(to, w)}
        />
      )}
    </>
  );

  return { onSendTo: setSending, onClear: setClearing, undo, sheets };
}
